import { inputToTags } from "../../../../mst/kinds/utils";
import { AwsPool, AwsProvider, AWSProviderDeployRoleChain } from "../../../../schema/types/mk8s/entity/aws";
import { AzurePool, AzureProvider } from "../../../../schema/types/mk8s/entity/azure";
import { UnmanagedPool } from "../../../../schema/types/mk8s/entity/common";
import { DigitalOceanPool, DigitalOceanProvider } from "../../../../schema/types/mk8s/entity/digitalocean";
import { EphemeralPool, EphemeralProvider } from "../../../../schema/types/mk8s/entity/ephemeral";
import { GcpPool, GcpProvider } from "../../../../schema/types/mk8s/entity/gcp";
import { GenericProvider } from "../../../../schema/types/mk8s/entity/generic";
import { HetznerPool, HetznerProvider } from "../../../../schema/types/mk8s/entity/hetzner";
import { LambdalabsPool, LambdalabsProvider } from "../../../../schema/types/mk8s/entity/lambdalabs";
import { LinodePool, LinodeProvider } from "../../../../schema/types/mk8s/entity/linode";
import { Mk8sAddons, Mk8sSpec } from "../../../../schema/types/mk8s/entity/mk8s";
import { OblivusPool, OblivusProvider } from "../../../../schema/types/mk8s/entity/oblivus";
import { PaperspacePool, PaperspaceProvider } from "../../../../schema/types/mk8s/entity/paperspace";
import {
  TritonPool,
  TritonProvider,
  TritonProviderLoadBalancer,
  TritonProviderSdcConnection,
} from "../../../../schema/types/mk8s/entity/triton";
import { AwsPoolInput, AwsProviderInput } from "../../../../schema/types/mk8s/input/aws";
import { AzurePoolInput, AzureProviderInput } from "../../../../schema/types/mk8s/input/azure";
import {
  AutoscalerConfigInput,
  NetworkingConfigInput,
  UnmanagedPoolInput,
} from "../../../../schema/types/mk8s/input/common";
import { DigitalOceanPoolInput, DigitalOceanProviderInput } from "../../../../schema/types/mk8s/input/digitalocean";
import { EphemeralPoolInput, EphemeralProviderInput } from "../../../../schema/types/mk8s/input/ephemeral";
import { GcpPoolInput, GcpProviderInput } from "../../../../schema/types/mk8s/input/gcp";
import { GenericProviderInput } from "../../../../schema/types/mk8s/input/generic";
import { HetznerPoolInput, HetznerProviderInput } from "../../../../schema/types/mk8s/input/hetzner";
import { LambdalabsPoolInput, LambdalabsProviderInput } from "../../../../schema/types/mk8s/input/lambdalabs";
import { LinodePoolInput, LinodeProviderInput } from "../../../../schema/types/mk8s/input/linode";
import { Mk8sInput } from "../../../../schema/types/mk8s/input/mk8s";
import { OblivusPoolInput, OblivusProviderInput } from "../../../../schema/types/mk8s/input/oblivus";
import { PaperspacePoolInput, PaperspaceProviderInput } from "../../../../schema/types/mk8s/input/paperspace";
import { TritonPoolInput, TritonProviderInput } from "../../../../schema/types/mk8s/input/triton";
import jsYaml from "js-yaml";

export interface Mk8sCreate {
  name: string;
  description: string;
  spec: Mk8sSpec;
  tags: Record<string, any>;
}

export function inputToNetworking(n: NetworkingConfigInput) {
  const res: any = {};
  if (n.serviceNetwork) {
    res.serviceNetwork = n.serviceNetwork;
  }
  if (n.podNetwork) {
    res.podNetwork = n.podNetwork;
  }
  if (n.dnsForwarder) {
    res.dnsForwarder = n.dnsForwarder;
  }
  return res;
}

export function inputToAutoscaler(a: AutoscalerConfigInput) {
  const res: any = {};
  if (a.expander.length > 0) {
    res.expander = a.expander;
  }
  if (a.unneededTime) {
    res.unneededTime = a.unneededTime;
  }
  if (a.unreadyTime) {
    res.unreadyTime = a.unreadyTime;
  }
  if (a.utilizationThreshold) {
    res.utilizationThreshold = Number(a.utilizationThreshold);
  }
  return res;
}

export function inputToAwsNodePool(np: AwsPoolInput): AwsPool {
  const res = {
    name: np.name,
    instanceTypes: np.instanceTypes.map((it) => it.value),
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    onDemandBaseCapacity: Number(np.onDemandBaseCapacity),
    onDemandPercentageAboveBaseCapacity: Number(np.onDemandPercentageAboveBaseCapacity),
    subnetIds: np.subnetIds.map((si) => si.value),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  } as AwsPool;

  if (np.bootDiskSize.length > 0) {
    res.bootDiskSize = Number(np.bootDiskSize);
  }

  if (np.overrideImage.type === "exact" && np.overrideImage.exact) {
    res.overrideImage = {
      exact: np.overrideImage.exact,
    };
  } else if (np.overrideImage.type === "recommended" && np.overrideImage.recommended) {
    res.overrideImage = {
      recommended: np.overrideImage.recommended as any,
    };
  }

  if (np.spotAllocationStrategy) {
    res.spotAllocationStrategy = np.spotAllocationStrategy as any;
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  if (np.extraSecurityGroupIds.length > 0) {
    res.extraSecurityGroupIds = np.extraSecurityGroupIds.map((si) => si.value);
  }

  if (np.cpuOptions.nestedVirtualization) {
    res.cpuOptions = { nestedVirtualization: true };
  }

  return res;
}

export function inputToAzureNodePool(np: AzurePoolInput): AzurePool {
  const res: AzurePool = {
    name: np.name,
    bootDiskSize: Number(np.bootDiskSize),
    size: np.size,
    subnetId: np.subnetId,
    zones: np.zones.filter((z) => z.length > 0).map((z) => Number(z)),
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  // none is ignored
  if (np.overrideImage.type === "recommended") {
    res.overrideImage = {
      recommended: np.overrideImage.recommended,
    };
  } else if (np.overrideImage.type === "reference") {
    res.overrideImage = {
      reference: {
        publisher: np.overrideImage.reference.publisher,
        offer: np.overrideImage.reference.offer,
        sku: np.overrideImage.reference.sku,
        version: np.overrideImage.reference.version,
      },
    };
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToUnmanagedNodePool(np: UnmanagedPoolInput): UnmanagedPool {
  const res: UnmanagedPool = {
    name: np.name,
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToDigitalOceanNodePool(np: DigitalOceanPoolInput): DigitalOceanPool {
  const res: DigitalOceanPool = {
    name: np.name,
    dropletSize: np.dropletSize,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  if (np.overrideImage) {
    res.overrideImage = np.overrideImage;
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToEphemeralNodePool(np: EphemeralPoolInput): EphemeralPool {
  const res: EphemeralPool = {
    name: np.name,
    arch: np.arch as any,
    count: Number(np.count),
    cpu: np.cpu,
    flavor: np.flavor as any,
    memory: np.memory,
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToGcpNodePool(np: GcpPoolInput): GcpPool {
  const res: GcpPool = {
    name: np.name,
    machineType: np.machineType,
    assignPublicIP: np.assignPublicIP,
    preemptible: np.preemptible,
    zone: np.zone,
    bootDiskSize: Number(np.bootDiskSize),
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    subnet: np.subnet,
    localPersistentDisks: Number(np.localPersistentDisks),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  if (np.overrideImage.type === "exact" && np.overrideImage.exact) {
    res.overrideImage = {
      exact: np.overrideImage.exact,
    };
  } else if (np.overrideImage.type === "recommended" && np.overrideImage.recommended) {
    res.overrideImage = {
      recommended: np.overrideImage.recommended as any,
    };
  } else if (np.overrideImage.type === "family") {
    res.overrideImage = {
      family: {
        project: np.overrideImage.family.project as any,
        family: np.overrideImage.family.family as any,
      },
    };
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToHetznerNodePool(np: HetznerPoolInput): HetznerPool {
  const res: HetznerPool = {
    name: np.name,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    serverType: np.serverType,
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  if (np.overrideImage) {
    res.overrideImage = np.overrideImage;
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToLambdalabsNodePool(np: LambdalabsPoolInput): LambdalabsPool {
  const res: LambdalabsPool = {
    name: np.name,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    instanceType: np.instanceType as any,
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToLinodeNodePool(np: LinodePoolInput): LinodePool {
  const res: LinodePool = {
    name: np.name,
    serverType: np.serverType,
    subnetId: np.subnetId,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  if (np.overrideImage) {
    res.overrideImage = np.overrideImage;
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToOblivusNodePool(np: OblivusPoolInput): OblivusPool {
  const res: OblivusPool = {
    name: np.name,
    flavor: np.flavor,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToPaperspaceNodePool(np: PaperspacePoolInput): PaperspacePool {
  const res = {
    name: np.name,
    machineType: np.machineType,
    publicIpType: np.publicIpType,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  } as PaperspacePool;

  if (np.bootDiskSize.length > 0) {
    res.bootDiskSize = Number(np.bootDiskSize);
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  return res;
}

export function inputToTritonNodePool(np: TritonPoolInput): TritonPool {
  const res: TritonPool = {
    name: np.name,
    packageId: np.packageId,
    minSize: Number(np.minSize),
    maxSize: Number(np.maxSize),
    taints: np.taints.map((l) => ({ key: l.key, value: l.value, effect: l.effect })),
  };

  if (np.overrideImageId) {
    res.overrideImageId = np.overrideImageId;
  }
  if (np.publicNetworkId) {
    res.publicNetworkId = np.publicNetworkId;
  }

  if (np.privateNetworkIds.length > 0) {
    res.privateNetworkIds = np.privateNetworkIds.map((pni) => pni.value);
  }

  const labels = inputToTags(np.labels);
  if (Object.keys(labels).length > 0) {
    res.labels = labels;
  }

  const tritonTags = inputToTags(np.tritonTags);
  if (Object.keys(tritonTags).length > 0) {
    res.tritonTags = tritonTags;
  }

  return res;
}

export function inputToAwsProvider(i: AwsProviderInput): AwsProvider {
  const awsProvider: AwsProvider = {
    deployRoleArn: i.deployRoleArn,
    image:
      i.image.type === "exact" && i.image.exact
        ? { exact: i.image.exact }
        : i.image.type === "recommended" && i.image.recommended
        ? { recommended: i.image.recommended }
        : ("" as any),
    region: i.region as any,
    vpcId: i.vpcId,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    awsProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    awsProvider.networking = networking;
  }

  if (i.preInstallScript) {
    awsProvider.preInstallScript = i.preInstallScript;
  }
  if (i.deployRoleChain.length > 0) {
    awsProvider.deployRoleChain = i.deployRoleChain.map((drc) => {
      const res: AWSProviderDeployRoleChain = { roleArn: drc.roleArn };
      if (drc.externalId) {
        res.externalId = drc.externalId;
      }
      if (drc.sessionNamePrefix) {
        res.sessionNamePrefix = drc.sessionNamePrefix;
      }
      return res;
    });
  }
  if (i.extraNodePolicies) {
    awsProvider.extraNodePolicies = i.extraNodePolicies.map((enp) => enp.value);
  }
  if (i.securityGroupIds) {
    awsProvider.securityGroupIds = i.securityGroupIds.map((sgi) => sgi.value);
  }
  if (i.keyPair) {
    awsProvider.keyPair = i.keyPair;
  }
  if (i.diskEncryptionKeyArn) {
    awsProvider.diskEncryptionKeyArn = i.diskEncryptionKeyArn;
  }
  if (i.awsTags.length > 0) {
    awsProvider.awsTags = inputToTags(i.awsTags);
  }
  if (i.skipCreateRoles) {
    awsProvider.skipCreateRoles = true;
  }
  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToAwsNodePool(np));
    awsProvider.nodePools = nodePools;
  }
  return awsProvider;
}

export function inputToAzureProvider(i: AzureProviderInput): AzureProvider {
  const azureProvider: AzureProvider = {
    location: i.location,
    networkId: i.networkId,
    resourceGroup: i.resourceGroup,
    sdkSecretLink: i.sdkSecretLink.link,
    subscriptionId: i.subscriptionId,
    image:
      i.image.type === "recommended"
        ? { recommended: i.image.recommended }
        : i.image.type === "reference"
        ? {
            reference: {
              publisher: i.image.reference.publisher,
              offer: i.image.reference.offer,
              sku: i.image.reference.sku,
              version: i.image.reference.version,
            },
          }
        : ("" as any),
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    azureProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    azureProvider.networking = networking;
  }

  if (i.preInstallScript) {
    azureProvider.preInstallScript = i.preInstallScript;
  }
  if (i.sshKeys.length > 0) {
    azureProvider.sshKeys = i.sshKeys.map((sk) => sk.value);
  }
  const tags = inputToTags(i.tags);
  if (Object.keys(tags).length > 0) {
    azureProvider.tags = tags;
  }
  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToAzureNodePool(np));
    azureProvider.nodePools = nodePools;
  }
  return azureProvider;
}

export function inputToDigitalOceanProvider(i: DigitalOceanProviderInput): DigitalOceanProvider {
  const digitaloceanProvider: DigitalOceanProvider = {
    image: i.image,
    region: i.region,
    tokenSecretLink: i.tokenSecretLink.link,
    vpcId: i.vpcId,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    digitaloceanProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    digitaloceanProvider.networking = networking;
  }

  if (i.preInstallScript) {
    digitaloceanProvider.preInstallScript = i.preInstallScript;
  }

  if (i.sshKeys.length > 0) {
    digitaloceanProvider.sshKeys = i.sshKeys.map((sk) => sk.value);
  }
  if (i.extraSshKeys.length > 0) {
    digitaloceanProvider.extraSshKeys = i.extraSshKeys.map((esk) => esk.value);
  }
  if (i.reservedIPs.length > 0) {
    digitaloceanProvider.reservedIPs = i.reservedIPs.map((esk) => esk.value);
  }
  const digitalOceanTags = inputToTags(i.digitalOceanTags);
  if (Object.keys(digitalOceanTags).length > 0) {
    digitaloceanProvider.digitalOceanTags = digitalOceanTags;
  }
  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToDigitalOceanNodePool(np));
    digitaloceanProvider.nodePools = nodePools;
  }
  return digitaloceanProvider;
}

export function inputToEphemeralProvider(i: EphemeralProviderInput): EphemeralProvider {
  const ephemeralProvider: EphemeralProvider = {
    location: i.location as any,
  };

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToEphemeralNodePool(np));
    ephemeralProvider.nodePools = nodePools;
  }
  return ephemeralProvider;
}

export function inputToGcpProvider(i: GcpProviderInput): GcpProvider {
  const gcpProvider: GcpProvider = {
    projectId: i.projectId,
    region: i.region,
    labels: inputToTags(i.labels),
    tags: i.tags.map((t) => t.value),
    metadata: inputToTags(i.metadata),
    network: i.network,
    saKeyLink: i.saKeyLink.link,
    image: {},
    nodePools: [],
  };

  if (i.image.type === "exact") {
    gcpProvider.image.exact = i.image.exact;
  } else if (i.image.type === "recommended") {
    gcpProvider.image.recommended = i.image.recommended;
  } else if (i.image.type === "family") {
    gcpProvider.image.family = {
      project: i.image.family.project,
      family: i.image.family.family,
    };
  }

  if (i.preInstallScript) {
    gcpProvider.preInstallScript = i.preInstallScript;
  }

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    gcpProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    gcpProvider.networking = networking;
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToGcpNodePool(np));
    gcpProvider.nodePools = nodePools;
  }
  return gcpProvider;
}

export function inputToGenericProvider(i: GenericProviderInput): GenericProvider {
  const genericProvider: GenericProvider = {
    location: i.location,
  };

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    genericProvider.networking = networking;
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToUnmanagedNodePool(np));
    genericProvider.nodePools = nodePools;
  }
  return genericProvider;
}

export function inputToHetznerProvider(i: HetznerProviderInput): HetznerProvider {
  const hetznerProvider: HetznerProvider = {
    image: i.image,
    networkId: i.networkId,
    region: i.region as any,
    tokenSecretLink: i.tokenSecretLink.link,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    hetznerProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    hetznerProvider.networking = networking;
  }

  if (i.firewallId) {
    hetznerProvider.firewallId = i.firewallId;
  }
  if (i.sshKey) {
    hetznerProvider.sshKey = i.sshKey;
  }

  if (i.preInstallScript) {
    hetznerProvider.preInstallScript = i.preInstallScript;
  }

  const floatingIPSelector = inputToTags(i.floatingIPSelector);
  if (Object.keys(floatingIPSelector).length > 0) {
    hetznerProvider.floatingIPSelector = floatingIPSelector;
  }
  const hetznerLabels = inputToTags(i.hetznerLabels);
  if (Object.keys(hetznerLabels).length > 0) {
    hetznerProvider.hetznerLabels = hetznerLabels;
  }
  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToHetznerNodePool(np));
    hetznerProvider.nodePools = nodePools;
  }
  if (i.dedicatedServerNodePools.length > 0) {
    const dedicatedServerNodePools = i.dedicatedServerNodePools.map((np) => inputToUnmanagedNodePool(np));
    hetznerProvider.dedicatedServerNodePools = dedicatedServerNodePools;
  }
  return hetznerProvider;
}

export function inputToLambdalabsProvider(i: LambdalabsProviderInput): LambdalabsProvider {
  const lambdalabsProvider: LambdalabsProvider = {
    sshKey: i.sshKey,
    region: i.region as any,
    tokenSecretLink: i.tokenSecretLink.link,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    lambdalabsProvider.autoscaler = autoscaler;
  }

  if (i.sshKey) {
    lambdalabsProvider.sshKey = i.sshKey;
  }

  if (i.preInstallScript) {
    lambdalabsProvider.preInstallScript = i.preInstallScript;
  }

  if (i.fileSystems.length > 0) {
    lambdalabsProvider.fileSystems = i.fileSystems.map((fs) => fs.value);
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToLambdalabsNodePool(np));
    lambdalabsProvider.nodePools = nodePools;
  }
  if (i.unmanagedNodePools.length > 0) {
    const unmanagedNodePools = i.unmanagedNodePools.map((np) => inputToUnmanagedNodePool(np));
    lambdalabsProvider.unmanagedNodePools = unmanagedNodePools;
  }
  return lambdalabsProvider;
}

export function inputToLinodeProvider(i: LinodeProviderInput): LinodeProvider {
  const linodeProvider: LinodeProvider = {
    image: i.image,
    region: i.region,
    vpcId: i.vpcId,
    tokenSecretLink: i.tokenSecretLink.link,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    linodeProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    linodeProvider.networking = networking;
  }

  if (i.firewallId) {
    linodeProvider.firewallId = i.firewallId;
  }

  if (i.preInstallScript) {
    linodeProvider.preInstallScript = i.preInstallScript;
  }

  if (i.authorizedKeys.length > 0) {
    linodeProvider.authorizedKeys = i.authorizedKeys.map((ak) => ak.value);
  }
  if (i.authorizedUsers.length > 0) {
    linodeProvider.authorizedUsers = i.authorizedUsers.map((au) => au.value);
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToLinodeNodePool(np));
    linodeProvider.nodePools = nodePools;
  }
  return linodeProvider;
}

export function inputToOblivusProvider(i: OblivusProviderInput): OblivusProvider {
  const oblivusProvider: OblivusProvider = {
    datacenter: i.datacenter,
    tokenSecretLink: i.tokenSecretLink.link,
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    oblivusProvider.autoscaler = autoscaler;
  }

  if (i.preInstallScript) {
    oblivusProvider.preInstallScript = i.preInstallScript;
  }

  if (i.sshKeys.length > 0) {
    oblivusProvider.sshKeys = i.sshKeys.map((sk) => sk.value);
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToOblivusNodePool(np));
    oblivusProvider.nodePools = nodePools;
  }
  if (i.unmanagedNodePools.length > 0) {
    const unmanagedNodePools = i.unmanagedNodePools.map((np) => inputToUnmanagedNodePool(np));
    oblivusProvider.unmanagedNodePools = unmanagedNodePools;
  }
  return oblivusProvider;
}

export function inputToPaperspaceProvider(i: PaperspaceProviderInput): PaperspaceProvider {
  const paperspaceProvider: PaperspaceProvider = {
    networkId: i.networkId,
    region: i.region,
    tokenSecretLink: i.tokenSecretLink.link,
    preInstallScript: "",
  };

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    paperspaceProvider.autoscaler = autoscaler;
  }

  if (i.preInstallScript) {
    paperspaceProvider.preInstallScript = i.preInstallScript;
  }

  if (i.sharedDrives.length > 0) {
    paperspaceProvider.sharedDrives = i.sharedDrives.map((sd) => sd.value);
  }
  if (i.userIds.length > 0) {
    paperspaceProvider.userIds = i.userIds.map((ui) => ui.value);
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToPaperspaceNodePool(np));
    paperspaceProvider.nodePools = nodePools;
  }
  if (i.unmanagedNodePools.length > 0) {
    const unmanagedNodePools = i.unmanagedNodePools.map((np) => inputToUnmanagedNodePool(np));
    paperspaceProvider.unmanagedNodePools = unmanagedNodePools;
  }
  return paperspaceProvider;
}

export function inputToTritonProvider(i: TritonProviderInput): TritonProvider {
  const connection: TritonProviderSdcConnection = {
    account: i.connection.account,
    privateKeySecretLink: i.connection.privateKeySecretLink.link,
    url: i.connection.url,
  };
  if (i.connection.user) {
    connection.user = i.connection.user;
  }
  const tritonProvider: TritonProvider = {
    connection: connection,
    imageId: i.imageId,
    location: i.location,
    privateNetworkId: i.privateNetworkId,
  };

  const loadBalancer: TritonProviderLoadBalancer = {};
  if (i.loadBalancer.type === "none") {
    loadBalancer.none = {};
    tritonProvider.loadBalancer = loadBalancer;
  } else if (i.loadBalancer.type === "gateway") {
    loadBalancer.gateway = {};
    tritonProvider.loadBalancer = loadBalancer;
  } else if (i.loadBalancer.type === "manual") {
    loadBalancer.manual = {
      cnsInternalDomain: i.loadBalancer.manual.cnsInternalDomain,
      cnsPublicDomain: i.loadBalancer.manual.cnsPublicDomain,
      imageId: i.loadBalancer.manual.imageId,
      packageId: i.loadBalancer.manual.packageId,
      publicNetworkId: i.loadBalancer.manual.publicNetworkId,
    } as any;
    if (i.loadBalancer.manual.count.length > 0) {
      loadBalancer.manual!.count = Number(i.loadBalancer.manual.count);
    }
    if (i.loadBalancer.manual.privateNetworkIds.length > 0) {
      loadBalancer.manual!.privateNetworkIds = i.loadBalancer.manual.privateNetworkIds.map((pni) => pni.value);
    }
    if (i.loadBalancer.manual.logging) {
      loadBalancer.manual!.logging = {} as any;
      if (i.loadBalancer.manual.logging.externalSyslog) {
        loadBalancer.manual!.logging!.externalSyslog = i.loadBalancer.manual.logging.externalSyslog;
      }
      if (i.loadBalancer.manual.logging.nodePort.length > 0) {
        loadBalancer.manual!.logging!.nodePort = Number(i.loadBalancer.manual.logging.nodePort);
      }
    }
    const metadata = inputToTags(i.loadBalancer.manual.metadata);
    if (Object.keys(metadata).length > 0) {
      loadBalancer.manual!.metadata = metadata;
    }
    const tags = inputToTags(i.loadBalancer.manual.tags);
    if (Object.keys(tags).length > 0) {
      loadBalancer.manual!.tags = tags;
    }
    tritonProvider.loadBalancer = loadBalancer;
  }

  if (i.firewallEnabled) {
    tritonProvider.firewallEnabled = true;
  }

  const autoscaler = inputToAutoscaler(i.autoscaler);
  if (Object.keys(autoscaler).length > 0) {
    tritonProvider.autoscaler = autoscaler;
  }

  const networking = inputToNetworking(i.networking);
  if (Object.keys(networking).length > 0) {
    tritonProvider.networking = networking;
  }

  if (i.preInstallScript) {
    tritonProvider.preInstallScript = i.preInstallScript;
  }

  if (i.sshKeys.length > 0) {
    tritonProvider.sshKeys = i.sshKeys.map((sk) => sk.value);
  }

  if (i.nodePools.length > 0) {
    const nodePools = i.nodePools.map((np) => inputToTritonNodePool(np));
    tritonProvider.nodePools = nodePools;
  }
  return tritonProvider;
}

export function inputToAddons(i: Mk8sInput): Mk8sAddons {
  const addOns: Mk8sAddons = {};
  if (i.addons.awsECR.isEnabled) {
    addOns.awsECR = {};
    if (i.addons.awsECR.roleArn) {
      addOns.awsECR.roleArn = i.addons.awsECR.roleArn;
    }
  }
  if (i.addons.awsEFS.isEnabled) {
    addOns.awsEFS = {
      roleArn: i.addons.awsEFS.roleArn,
    };
  }
  if (i.addons.awsELB.isEnabled) {
    addOns.awsELB = {};
    if (i.addons.awsELB.roleArn) {
      addOns.awsELB.roleArn = i.addons.awsELB.roleArn;
    }
  }
  // TODO fix this with code editor
  if (i.addons.awsWorkloadIdentity.isEnabled) {
    addOns.awsWorkloadIdentity = {};
  }
  if (i.addons.azureACR.isEnabled) {
    addOns.azureACR = {
      clientId: i.addons.azureACR.clientId,
    };
  }
  if (i.addons.azureWorkloadIdentity.isEnabled) {
    addOns.azureWorkloadIdentity = {};
    if (i.addons.azureWorkloadIdentity.tenantId) {
      addOns.azureWorkloadIdentity.tenantId = i.addons.azureWorkloadIdentity.tenantId;
    }
  }
  if (i.addons.byok.isEnabled) {
    let config = {};
    try {
      config = jsYaml.load(i.addons.byok.config) as any;
    } catch (e) {}
    addOns.byok = {
      location: i.addons.byok.location.link,
      ignoreUpdates: i.addons.byok.ignoreUpdates,
      config: config,
    };
  }
  if (i.addons.dashboard.isEnabled) {
    addOns.dashboard = {};
  }
  if (i.addons.headlamp.isEnabled) {
    addOns.headlamp = {};
  }
  // TODO fix this with code editor
  if (i.addons.localPathStorage.isEnabled) {
    addOns.localPathStorage = {};
  }
  if (i.addons.logs.isEnabled) {
    addOns.logs = {
      auditEnabled: i.addons.logs.auditEnabled,
      docker: i.addons.logs.docker,
      events: i.addons.logs.events,
      kubelet: i.addons.logs.kubelet,
      kernel: i.addons.logs.kernel,
    };
    if (i.addons.logs.includeNamespaces) {
      addOns.logs.includeNamespaces = i.addons.logs.includeNamespaces;
    }
    if (i.addons.logs.excludeNamespaces) {
      addOns.logs.excludeNamespaces = i.addons.logs.excludeNamespaces;
    }
  }
  if (i.addons.metrics.isEnabled) {
    addOns.metrics = {
      apiserver: i.addons.metrics.apiserver,
      cadvisor: i.addons.metrics.cadvisor,
      coreDns: i.addons.metrics.coreDns,
      kubeState: i.addons.metrics.kubeState,
      kubelet: i.addons.metrics.kubelet,
      nodeExporter: i.addons.metrics.nodeExporter,
    };
    if (i.addons.metrics.scrapeAnnotated.isEnabled) {
      addOns.metrics.scrapeAnnotated = {};
      if (i.addons.metrics.scrapeAnnotated.includeNamespaces) {
        addOns.metrics.scrapeAnnotated.includeNamespaces = i.addons.metrics.scrapeAnnotated.includeNamespaces;
      }
      if (i.addons.metrics.scrapeAnnotated.excludeNamespaces) {
        addOns.metrics.scrapeAnnotated.excludeNamespaces = i.addons.metrics.scrapeAnnotated.excludeNamespaces;
      }
      if (i.addons.metrics.scrapeAnnotated.intervalSeconds.length > 0) {
        addOns.metrics.scrapeAnnotated.intervalSeconds = Number(i.addons.metrics.scrapeAnnotated.intervalSeconds);
      }
      if (i.addons.metrics.scrapeAnnotated.retainLabels) {
        addOns.metrics.scrapeAnnotated.retainLabels = i.addons.metrics.scrapeAnnotated.retainLabels;
      }
    }
  }
  if (i.addons.nvidia.isEnabled) {
    addOns.nvidia = {
      taintGPUNodes: i.addons.nvidia.taintGPUNodes,
    };
  }
  if (i.addons.registryMirror.isEnabled) {
    addOns.registryMirror = {
      mirrors: i.addons.registryMirror.mirrors.map((m) => ({ registry: m.registry, mirrors: [m.mirror] })),
    };
  }
  // TODO fix this with code editor
  if (i.addons.sysbox.isEnabled) {
    addOns.sysbox = {};
  }
  return addOns;
}

export function inputToMk8s(i: Mk8sInput): Mk8sCreate {
  const spec: Mk8sSpec = {
    version: i.specVersion,
    provider: {},
  };

  if (i.provider === "aws") {
    const awsProvider = inputToAwsProvider(i.aws);
    spec.provider.aws = awsProvider;
  }

  if (i.provider === "azure") {
    const azureProvider = inputToAzureProvider(i.azure);
    spec.provider.azure = azureProvider;
  }

  if (i.provider === "digitalocean") {
    const digitaloceanProvider = inputToDigitalOceanProvider(i.digitalocean);
    spec.provider.digitalocean = digitaloceanProvider;
  }

  if (i.provider === "ephemeral") {
    const ephemeralProvider = inputToEphemeralProvider(i.ephemeral);
    spec.provider.ephemeral = ephemeralProvider;
  }

  if (i.provider === "gcp") {
    const gcpProvider = inputToGcpProvider(i.gcp);
    spec.provider.gcp = gcpProvider;
  }

  if (i.provider === "generic") {
    const genericProvider = inputToGenericProvider(i.generic);
    spec.provider.generic = genericProvider;
  }

  if (i.provider === "hetzner") {
    const hetznerProvider = inputToHetznerProvider(i.hetzner);
    spec.provider.hetzner = hetznerProvider;
  }

  if (i.provider === "lambdalabs") {
    const lambdalabsProvider = inputToLambdalabsProvider(i.lambdalabs);
    spec.provider.lambdalabs = lambdalabsProvider;
  }

  if (i.provider === "linode") {
    const linodeProvider = inputToLinodeProvider(i.linode);
    spec.provider.linode = linodeProvider;
  }

  if (i.provider === "oblivus") {
    const oblivusProvider = inputToOblivusProvider(i.oblivus);
    spec.provider.oblivus = oblivusProvider;
  }

  if (i.provider === "paperspace") {
    const paperspaceProvider = inputToPaperspaceProvider(i.paperspace);
    spec.provider.paperspace = paperspaceProvider;
  }

  if (i.provider === "triton") {
    const tritonProvider = inputToTritonProvider(i.triton);
    spec.provider.triton = tritonProvider;
  }

  spec.addOns = inputToAddons(i);

  // firewall
  if (i.firewall.length > 0) {
    spec.firewall = i.firewall.map((f) => ({ sourceCIDR: f.sourceCIDR.trim(), description: f.description || "" }));
  }

  const res: Mk8sCreate = {
    name: i.name,
    description: i.description || i.name,
    spec: spec,
    tags: inputToTags(i.tags),
  };

  return res;
}
